import { useMemo, useState } from 'react'
import { endOfDay, startOfDay } from 'date-fns'
import {
  PiCurrencyDollar,
  PiTrophy,
  PiTrendUp,
  PiPercent,
} from 'react-icons/pi'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  StatsWidget,
  TrendsWidget,
  PipelineWidget,
  Card,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
  Skeleton,
  type PerformanceStat,
  type PerformanceTrends,
  type LineChartData,
  type PeriodValue,
} from '@firstbite/ui'
import {
  getHomepageStats,
  getPerformanceStats,
  getPipelineValueStats,
  type HomepageStats,
  type PerformanceStats,
  type PipelineValueStats,
} from '../../services/recommendations_api'
import {
  formatCurrency,
  formatInteger,
  formatPercentage,
} from '../../utils/formatting'
import useApiService from '../../services/api'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import {
  buildQuickSelects,
  defaultPeriodFor,
  resolveAppliedPeriod,
  toPipelineData,
} from './performanceDashboardData'

/**
 * PerformanceDashboardCardFbUi — the homepage "Performance" section rendered with
 * the `@firstbite/ui` performance widgets (Stats / Trends / Pipeline by stage),
 * replacing the hand-rolled Chart.js dashboard. It reuses the SAME data endpoints
 * (`getPerformanceStats`, `getHomepageStats`, `getPipelineValueStats`) and the
 * `userId` prop — no backend change — and just reshapes their responses into the
 * widgets' data props.
 *
 * Period is scoped PER WIDGET: Stats and Trends each carry their own simplified
 * picker (a range calendar + our preset quick-selects, no fiscal modes) and their
 * own period state + query, so changing one widget's window does not affect the
 * other. Pipeline by stage is a live snapshot of open deals (its endpoint takes
 * no dates), so it has no period picker at all.
 */

interface PerformanceDashboardCardFbUiProps {
  /** Optional user ID for superusers to view another user's performance. */
  userId?: number
}

/**
 * First-load skeleton — mirrors the three-widget grid (Stats column + Trends and
 * Pipeline cards) with fb-ui `Skeleton` blocks so the layout doesn't shift when
 * the real data lands. Uses the same Card chrome, grid, and heights as the loaded
 * dashboard below.
 */
function PerformanceDashboardSkeleton() {
  const periodPill = <Skeleton className="h-9 w-36 rounded-lg" />
  return (
    <div className="grid grid-cols-1 gap-[1rem] lg:grid-cols-3">
      {/* Stats — four stacked KPI tiles. */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>
            <Skeleton className="h-5 w-16" />
          </CardTitle>
          <CardAction>{periodPill}</CardAction>
        </CardHeader>
        <CardContent className="flex flex-col gap-[1rem]">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-2.5">
              <Skeleton className="size-9 shrink-0 rounded-xl" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-5 w-14" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Trends + Pipeline column. */}
      <div className="flex flex-col gap-[1rem] lg:col-span-2">
        <Card className="flex h-80 flex-col">
          <CardHeader className="pb-2">
            <CardTitle>
              <Skeleton className="h-5 w-20" />
            </CardTitle>
            <CardAction>{periodPill}</CardAction>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-[1.25rem]">
            <div className="flex gap-6 border-b border-fbui-gray-200 pb-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-5 w-12" />
              <Skeleton className="h-5 w-16" />
            </div>
            <Skeleton className="min-h-0 flex-1 rounded-lg" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <Skeleton className="h-5 w-32" />
            </CardTitle>
            <CardAction>{periodPill}</CardAction>
          </CardHeader>
          <CardContent className="flex flex-col gap-[1rem]">
            <Skeleton className="h-3 w-full rounded-full" />
            <div className="grid grid-cols-2 gap-x-12 gap-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

/**
 * Period → the shared query params + queryKey + enabled gate. Each widget calls
 * this with its own period so React Query caches and refetches them
 * independently (identical periods collapse to one entry via the identical key).
 */
function periodToQuery(userId: number | undefined, period: PeriodValue) {
  const startDate = period.start
    ? startOfDay(period.start).toISOString()
    : undefined
  const endDate = period.end ? endOfDay(period.end).toISOString() : undefined
  return {
    params: {
      userId,
      periodType: 'monthly' as const,
      range: 'custom' as const,
      startDate,
      endDate,
    },
    key: [userId ?? 'self', startDate ?? null, endDate ?? null],
    enabled: !!startDate && !!endDate,
  }
}

export function PerformanceDashboardCardFbUi({
  userId,
}: PerformanceDashboardCardFbUiProps) {
  // Picker quick-selects, built once for the picker UI. Their windows are frozen
  // at mount, so the actual apply goes through `resolveAppliedPeriod` (below),
  // which recomputes a preset's window from a fresh `now` at click time.
  const quickSelects = useMemo(() => buildQuickSelects(new Date()), [])

  // Per-widget periods (default "Last 6 months", computed once on mount). Stats
  // and Trends are independent so one picker never reloads the other.
  const [statsPeriod, setStatsPeriod] = useState<PeriodValue>(() =>
    defaultPeriodFor(new Date())
  )
  const [trendsPeriod, setTrendsPeriod] = useState<PeriodValue>(() =>
    defaultPeriodFor(new Date())
  )
  // Re-clicking a preset later (a tab left open across a day boundary) must not
  // apply the stale mount-time window — recompute presets from `now` on apply.
  const applyStatsPeriod = (value: PeriodValue) =>
    setStatsPeriod(resolveAppliedPeriod(value, new Date()))
  const applyTrendsPeriod = (value: PeriodValue) =>
    setTrendsPeriod(resolveAppliedPeriod(value, new Date()))

  const statsQuery = periodToQuery(userId, statsPeriod)
  const trendsQuery = periodToQuery(userId, trendsPeriod)

  // Stats KPI totals — scoped to the Stats picker.
  const { data: statsData, isLoading: statsLoading } =
    useQuery<PerformanceStats | null>({
      queryKey: ['performance-stats', ...statsQuery.key],
      queryFn: () => getPerformanceStats(statsQuery.params),
      enabled: statsQuery.enabled,
      placeholderData: keepPreviousData,
    })
  // Trends time-series — scoped to the Trends picker. On first load both periods
  // equal the default, so this shares Stats' cache entry (identical queryKey);
  // they split into separate fetches only once the user diverges one.
  const { data: trendsStats } = useQuery<PerformanceStats | null>({
    queryKey: ['performance-stats', ...trendsQuery.key],
    queryFn: () => getPerformanceStats(trendsQuery.params),
    enabled: trendsQuery.enabled,
    placeholderData: keepPreviousData,
  })
  const { data: pipelineValueStats } = useQuery<PipelineValueStats | null>({
    queryKey: ['pipeline-value-stats', ...trendsQuery.key],
    queryFn: () => getPipelineValueStats(trendsQuery.params),
    enabled: trendsQuery.enabled,
    placeholderData: keepPreviousData,
  })
  // Pipeline by stage — a current snapshot of open deals; NOT period-scoped.
  const { data: pipelineStats } = useQuery<HomepageStats | null>({
    queryKey: ['homepage-stats', userId ?? 'self'],
    queryFn: () => getHomepageStats(userId),
  })

  const { data: allStages } = useApiService().useGetSalesStages()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  // API periods arrive newest-first; charts read oldest → left.
  const ordered = useMemo(
    () => [...(trendsStats?.periods ?? [])].reverse(),
    [trendsStats]
  )
  const pipeOrdered = useMemo(
    () => [...(pipelineValueStats?.periods ?? [])].reverse(),
    [pipelineValueStats]
  )

  const performanceStats: PerformanceStat[] = statsData
    ? [
        {
          key: 'deals-opened',
          label: 'Deals Opened',
          icon: <PiTrendUp />,
          value: formatInteger(statsData.totals.deals_opened),
          help: 'Deals opened in the selected period.',
        },
        {
          key: 'deals-won',
          label: 'Deals Won',
          icon: <PiTrophy />,
          value: formatInteger(statsData.totals.deals_won),
          help: 'Deals closed won in the selected period.',
        },
        {
          key: 'value-won',
          label: 'Value Won',
          icon: <PiCurrencyDollar />,
          value: formatCurrency(statsData.totals.value_won),
          help: 'Annual value of deals won in the selected period.',
        },
        {
          key: 'win-rate',
          label: 'Win Rate',
          icon: <PiPercent />,
          value:
            statsData.totals.overall_win_rate !== null
              ? `${formatPercentage(statsData.totals.overall_win_rate)}%`
              : '-',
          help: 'Win rate — Won / (Won + Lost) — for the selected period.',
        },
      ]
    : []

  const activity: PerformanceTrends = {
    series: [
      { key: 'deals', label: 'Deals', color: 'primary' },
      { key: 'emails', label: 'Emails', color: 'sky' },
    ],
    points: ordered.map((p) => ({
      label: p.period_label,
      values: [p.deals_opened, p.emails_sent],
    })),
    formatValue: (n) => formatInteger(n),
  }

  const valueTrend: LineChartData = {
    labels: ordered.map((p) => p.period_label),
    series: [
      {
        key: 'won',
        label: 'Value Won',
        color: 'primary',
        values: ordered.map((p) => p.value_won),
      },
      {
        key: 'opened',
        label: 'Value Opened',
        color: 'sky',
        values: ordered.map((p) => p.value_opened),
      },
    ],
    leftFormat: (n) => formatCurrency(n),
  }

  const pipelineTrend: LineChartData = {
    labels: pipeOrdered.map((p) => p.period_label),
    series: [
      {
        key: 'unweighted',
        label: 'Pipeline value',
        color: 'indigo',
        values: pipeOrdered.map((p) => p.unweighted_value),
      },
      {
        key: 'weighted',
        label: 'Weighted value',
        color: 'violet',
        values: pipeOrdered.map((p) => p.weighted_value),
      },
      {
        key: 'count',
        label: 'Deals',
        color: 'primary',
        values: pipeOrdered.map((p) => p.deal_count),
        axis: 'right',
      },
    ],
    leftFormat: (n) => formatCurrency(n),
    rightFormat: (n) => formatInteger(n),
  }

  const pipeline = useMemo(
    () => toPipelineData(pipelineStats, allStages, customOrderEnabled),
    [pipelineStats, allStages, customOrderEnabled]
  )

  // First load (no data cached yet) → mirror the widgets' layout with skeletons
  // so nothing shifts when data lands. keepPreviousData keeps the prior data on
  // screen across period changes, so this only shows on the very first load.
  // On a hard error (not loading, still no data) fall through to `null`.
  if (!statsData) return statsLoading ? <PerformanceDashboardSkeleton /> : null

  return (
    <div className="grid grid-cols-1 gap-[1rem] lg:grid-cols-3">
      <StatsWidget
        quickSelects={quickSelects}
        showModes={false}
        defaultPeriod={statsPeriod}
        onPeriodApply={applyStatsPeriod}
        stats={performanceStats}
        statColumns={1}
        className="max-w-none lg:col-span-1"
      />
      <div className="flex flex-col gap-[1rem] lg:col-span-2">
        <TrendsWidget
          quickSelects={quickSelects}
          showModes={false}
          defaultPeriod={trendsPeriod}
          onPeriodApply={applyTrendsPeriod}
          activity={activity}
          value={valueTrend}
          pipeline={pipelineTrend}
          className="h-80 max-w-none"
        />
        {/* No period picker — pipeline by stage is a live snapshot, not
            period-scoped (see the component doc). */}
        <PipelineWidget pipeline={pipeline} className="max-w-none" />
      </div>
    </div>
  )
}
